import { useFormContext } from "react-hook-form";
import { FormControl, FormField, FormItem, FormMessage } from "@shadcn/components/ui/form";
import { Input } from "@shadcn/components/ui/input";

type VehicleSearchManualEntryProps = {
  name: string;
  required: boolean;
  disabled?: boolean;
};

export const VehicleSearchManualEntry = ({ name, required, disabled }: VehicleSearchManualEntryProps) => {
  const { control, setValue, watch } = useFormContext();
  const watchedLicencePlate = watch(`${name}.licencePlate`);
  const watchedMake = watch(`${name}.make`);
  const watchedModel = watch(`${name}.model`);
  const watchedColour = watch(`${name}.colour`);
  const watchedVin = watch(`${name}.vin`);

  // Validation: assumes we're in manual mode, only validates manual entry fields
  const validateManualEntry = () => {
    if (!watchedLicencePlate || watchedLicencePlate.trim() === "") {
      return "Registration plate is required";
    }
    if (!watchedMake || watchedMake.trim() === "") {
      return "Make is required";
    }
    if (!watchedModel || watchedModel.trim() === "") {
      return "Model is required";
    }
    if (!watchedColour || watchedColour.trim() === "") {
      return "Colour is required";
    }
    // All fields filled - mark as valid by ensuring vin has a value and isManualEntry flag is set
    if (!watchedVin || watchedVin === "manual-entry") {
      setValue(`${name}.vin`, "manual-entry");
    }
    setValue(`${name}.isManualEntry`, true);
    return true;
  };

  return (
    <>
      {/* Hidden validation field - validates manual entry fields */}
      <FormField
        control={control}
        name={`${name}.vin`}
        rules={required && { validate: validateManualEntry }}
        render={() => <></>}
      />
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={control}
            name={`${name}.licencePlate`}
            rules={required && { required: "Registration plate is required" }}
            render={({ field }) => (
              <FormItem>
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Registration Plate
                </label>
                <FormControl>
                  <Input {...field} disabled={disabled} onChange={(e) => field.onChange(e.target.value.replace(/[^a-zA-Z0-9 ]/g, "").toUpperCase())} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={`${name}.colour`}
            rules={required && { required: "Colour is required" }}
            render={({ field }) => (
              <FormItem>
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Colour
                </label>
                <FormControl>
                  <Input {...field} disabled={disabled} onChange={(e) => field.onChange(e.target.value.replace(/[^a-zA-Z0-9 ]/g, ""))} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={control}
            name={`${name}.make`}
            rules={required && { required: "Make is required" }}
            render={({ field }) => (
              <FormItem>
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Make
                </label>
                <FormControl>
                  <Input {...field} disabled={disabled} onChange={(e) => field.onChange(e.target.value.replace(/[^a-zA-Z0-9 ]/g, ""))} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={`${name}.model`}
            rules={required && { required: "Model is required" }}
            render={({ field }) => (
              <FormItem>
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Model
                </label>
                <FormControl>
                  <Input {...field} disabled={disabled} onChange={(e) => field.onChange(e.target.value.replace(/[^a-zA-Z0-9 ]/g, ""))} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={control}
            name={`${name}.yearOfManufacture`}
            rules={required && { required: "Year is required" }}
            render={({ field }) => (
              <FormItem>
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Year
                </label>
                <FormControl>
                  <Input {...field} disabled={disabled} placeholder="e.g. 2019" inputMode="numeric" onChange={(e) => field.onChange(e.target.value.replace(/[^0-9]/g, ""))} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </>
  );
};

